import React, { useEffect, useRef, useState, useMemo } from 'react';
import { StyledMasonry, MasonryColumn } from './Masonry.style';

export interface MasonryProps {
  /**
   * Number of columns for the masonry layout
   */
  columns?: number;
  /**
   * Responsive columns configuration
   * Object with breakpoints as keys and column counts as values
   */
  breakpoints?: Record<number, number>;
  /**
   * Gap between masonry items in pixels
   */
  gap?: number;
  /**
   * Minimum column width in pixels
   */
  minColumnWidth?: number;
  /**
   * Children elements to be arranged in masonry layout
   */
  children: React.ReactNode;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
  /**
   * Whether to use CSS columns (default) or JavaScript-based layout
   */
  method?: 'css' | 'js';
  /**
   * Animation duration for transitions
   */
  transitionDuration?: number;
  /**
   * Enable animations for masonry items
   */
  animated?: boolean;
  /**
   * Animation type for item entrance
   */
  animationType?: 'fade' | 'scale' | 'slideUp' | 'slideDown' | 'fadeScale';
  /**
   * Stagger delay between item animations (in ms)
   */
  staggerDelay?: number;
  /**
   * Enable hover animations on items
   */
  hoverAnimation?: boolean;
  /**
   * Custom animation duration override (in seconds)
   */
  animationDuration?: number;
  /**
   * Make all items have the same height as the tallest item
   */
  uniformHeight?: boolean;
}

export const Masonry: React.FC<MasonryProps> = ({
  columns = 3,
  breakpoints,
  gap = 16,
  minColumnWidth = 250,
  children,
  className,
  style,
  method = 'css',
  transitionDuration = 0.3,
  animated = false,
  animationType = 'slideUp',
  staggerDelay = 100,
  hoverAnimation = false,
  animationDuration = 0.6,
  uniformHeight = false,
}) => {
  const [currentColumns, setCurrentColumns] = useState(columns);
  const masonryRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<React.ReactNode[]>([]);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  // Convert children to array (memoized to prevent infinite re-renders)
  const childrenArray = useMemo(() => React.Children.toArray(children), [children]);

  // Handle responsive columns
  useEffect(() => {
    if (!breakpoints) return;

    const updateColumns = () => {
      const width = window.innerWidth;
      const sortedBreakpoints = Object.keys(breakpoints)
        .map(Number)
        .sort((a, b) => b - a);

      for (const breakpoint of sortedBreakpoints) {
        if (width >= breakpoint) {
          setCurrentColumns(breakpoints[breakpoint]);
          break;
        }
      }
    };

    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, [breakpoints]);

  // Handle auto columns based on minimum width
  useEffect(() => {
    if (breakpoints || !minColumnWidth) return;

    const updateColumns = () => {
      if (masonryRef.current) {
        const containerWidth = masonryRef.current.offsetWidth;
        const calculatedColumns = Math.floor(containerWidth / minColumnWidth);
        setCurrentColumns(Math.max(1, calculatedColumns));
      }
    };

    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, [minColumnWidth, breakpoints]);

  // Distribute items across columns for both methods (left-to-right arrangement)
  useEffect(() => {
    // For both CSS and JS methods, we'll use JavaScript-based distribution
    // to ensure left-to-right arrangement
    const columns: React.ReactNode[][] = Array.from({ length: currentColumns }, () => []);

    childrenArray.forEach((child, index) => {
      // Distribute items left to right (round-robin)
      const columnIndex = index % currentColumns;
      columns[columnIndex].push(child);
    });

    setItems(columns);
  }, [childrenArray, currentColumns, method]);

  // Handle uniform height measurement
  useEffect(() => {
    if (!uniformHeight || !masonryRef.current) {
      setMaxHeight(undefined);
      return;
    }

    const measureHeights = () => {
      const itemElements = masonryRef.current?.querySelectorAll('[data-masonry-item]');
      if (!itemElements || itemElements.length === 0) return;

      let tallestHeight = 0;
      itemElements.forEach((element) => {
        const height = (element as HTMLElement).offsetHeight;
        if (height > tallestHeight) {
          tallestHeight = height;
        }
      });

      if (tallestHeight > 0) {
        setMaxHeight(tallestHeight);
      }
    };

    // Measure heights after a short delay to ensure items are rendered
    const timeoutId = setTimeout(measureHeights, 100);

    // Also measure on window resize
    const handleResize = () => {
      setMaxHeight(undefined);
      setTimeout(measureHeights, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [uniformHeight, items, currentColumns]);

  // Both methods now use the same column-based rendering for left-to-right arrangement
  return (
    <StyledMasonry
      ref={masonryRef}
      className={className}
      style={style}
      method={method}
      gap={gap}
      transitionDuration={transitionDuration}
      animated={animated}
      animationType={animationType}
      staggerDelay={staggerDelay}
      hoverAnimation={hoverAnimation}
      animationDuration={animationDuration}
      uniformHeight={uniformHeight}
      maxHeight={maxHeight}
    >
      {(items as React.ReactNode[][]).map((columnItems, columnIndex) => (
        <MasonryColumn 
          key={columnIndex} 
          gap={gap}
          animated={animated}
          animationType={animationType}
          staggerDelay={staggerDelay}
          hoverAnimation={hoverAnimation}
          animationDuration={animationDuration}
          columnIndex={columnIndex}
          uniformHeight={uniformHeight}
          maxHeight={maxHeight}
        >
          {columnItems.map((item, itemIndex) => (
            <div key={itemIndex} data-masonry-item>
              {item}
            </div>
          ))}
        </MasonryColumn>
      ))}
    </StyledMasonry>
  );
}; 